import { FC } from 'react';
import { Box, Button, Heading, Stack, Text } from '@chakra-ui/react';
import { BiTimeFive } from 'react-icons/bi';

import { useSearchParams } from 'react-router-dom';
import { FileDetailsFileFragment } from '../../graphql/generated';
import { formatTimestamp } from '../../utils/date/date';
import { formatFileSize } from '../../utils/file/file';
import { Placeholder } from '../file-browser/file-browser';
import { useVersionHistoryDrawer } from '../file-version-history/version-history-drawer';
import { Property } from '../property/property';
import { PiiApprovalsList } from './pii-approvals-list';

type Props = {
  file: FileDetailsFileFragment;
};

export const FileMetadata: FC<Props> = ({ file }) => {
  const [searchParams] = useSearchParams();
  const versionHistory = searchParams.get('versionHistory');

  const { openDrawer } = useVersionHistoryDrawer(Boolean(versionHistory));
  const {
    createdBy,
    updatedAt,
    size,
    piiDetails,
    createdAt,
    automatedUpload,
  } = file;

  return (
    <Box>
      <Heading as="div" size="lg" mb="4">
        Details
      </Heading>
      <Stack spacing={6} data-testid="file-properties">
        <Property label="Created">
          <Text data-testid="created-at">
            {createdAt ? formatTimestamp(createdAt) : <Placeholder />}
          </Text>
          <Text data-testid="created-by" color="gray.500">
            {'by '}
            {automatedUpload
              ? 'Automated Upload'
              : createdBy?.name || <Placeholder />}
          </Text>
        </Property>
        {updatedAt && file.versions.length > 1 && (
          <Property label="Revised">
            <Text data-testid="updated-at">{formatTimestamp(updatedAt)}</Text>
            <Text data-testid="updated-by" color="gray.500">
              by {file.versions[0].updatedBy?.name || <Placeholder />}
            </Text>
            <Button
              mt={2}
              variant="link"
              colorScheme="blue"
              onClick={openDrawer}
              rightIcon={<BiTimeFive />}
              data-testid="open-version-history-button"
            >
              View revisions
            </Button>
          </Property>
        )}

        <Property label="Size" testId="file-size">
          {formatFileSize(size)}
        </Property>

        {piiDetails && <PiiApprovalsList approvals={piiDetails.approvals} />}
      </Stack>
    </Box>
  );
};
